// Cinematic festival graphics — real laurel assets (provided by the client).
// Gold laurels & White laurels are used ONLY as small, elegant emblems flanking
// the prize label. The prize name / colour / size are dynamic (set per film in admin).

export const LAUREL_GOLD = "/laurel-gold.png";
export const LAUREL_WHITE = "/laurel-white.png";
export const LAUREL_BRONZE = "/laurel-bronze.png";
// Award tiers: gold (Grand Prix), bronze (Prix du Public par défaut), white (autres prix).
export const AWARD_TIERS = ["gold", "bronze", "white"];
const AWARD_TEXT = { gold: "#e7c56a", bronze: "#cd8f52", white: "#ececec" };
const normColor = (c) => (AWARD_TIERS.includes(c) ? c : "gold");
// Auto tier inference from the prize name. For the canonical prizes this takes
// precedence over any stale stored colour so identity is truly automatic:
// « Prix du Public » → bronze ; « Prix/Grand Prix du Jury » → or. Unknown → null.
const tierFromName = (name) => {
  const n = (name || "").toLowerCase();
  if (/public/.test(n)) return "bronze";
  if (/jury|jur[yé]|grand\s*prix/.test(n)) return "gold";
  return null;
};
export const laurelSrc = (color) => (color === "white" ? LAUREL_WHITE : color === "bronze" ? LAUREL_BRONZE : LAUREL_GOLD);

// Default, premium identity per prize tier (contour + subtle glow). Editable in admin.
export const DEFAULT_PRIZE_STYLES = {
  gold: { label: "Or — Prix du Jury", borderEnabled: true, borderColor: "#e7c56a", glowEnabled: true, glowColor: "#e7c56a", intensity: 0.5 },
  bronze: { label: "Bronze — Prix du Public", borderEnabled: true, borderColor: "#cd8f52", glowEnabled: true, glowColor: "#cd8f52", intensity: 0.5 },
  white: { label: "Argent — autres prix", borderEnabled: true, borderColor: "#dfe3ea", glowEnabled: true, glowColor: "#dfe3ea", intensity: 0.35 },
};

// Resolve the effective style for an award tier (defaults merged with admin overrides).
export const resolvePrizeStyle = (award, config = {}) => {
  const tier = normColor(award?.color);
  const base = DEFAULT_PRIZE_STYLES[tier] || DEFAULT_PRIZE_STYLES.gold;
  return { tier, ...base, ...((config && config[tier]) || {}) };
};

// Inline style applied to the WHOLE primed-film card (border colour + subtle glow).
export const prizeCardStyle = (award, config) => {
  if (!award) return null;
  const s = resolvePrizeStyle(award, config);
  const style = {};
  if (s.borderEnabled) style.borderColor = s.borderColor;
  const it = Number(s.intensity) || 0;
  if (s.glowEnabled && it > 0) {
    style.boxShadow = `0 0 ${Math.round(it * 42)}px ${Math.round(it * -6)}px color-mix(in srgb, ${s.glowColor} ${Math.round(35 + it * 30)}%, transparent)`;
  }
  return style;
};

// Name that becomes a discreet link when a URL is provided (director / structure).
export const NameLink = ({ name, url, className = "" }) => {
  if (!name) return null;
  if (!url) return <span className={className}>{name}</span>;
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`underline decoration-white/30 underline-offset-2 hover:text-electric transition-colors ${className}`}>{name}</a>;
};

// Normalized single award of a film (or null if none / hidden / empty name).
export const filmAward = (film) => {
  const a = film?.award;
  if (!a || a.show === false || !(a.name && String(a.name).trim())) return null;
  const color = tierFromName(a.name) || (AWARD_TIERS.includes(a.color) ? a.color : "gold");
  return { name: String(a.name).trim(), color, size: a.size || 1, principal: !!a.principal, juryNote: (a.juryNote || "").trim() };
};

// Small, elegant award label: ‹laurel› NAME ‹laurel›. Text stays dynamic & sized by admin.
export const AwardLabel = ({ name, color = "gold", size = 1, className = "", wrap = false, testid }) => {
  const c = normColor(color);
  const fs = Math.max(9, Math.round(11 * size));
  const lh = Math.round(fs * 1.5);
  const src = laurelSrc(c);
  // Graceful fallback to gold laurel if the bronze asset is not uploaded yet.
  const onErr = (e) => { if (e.currentTarget.src.indexOf(LAUREL_GOLD) === -1) e.currentTarget.src = LAUREL_GOLD; };
  return (
    <span data-testid={testid} className={`inline-flex items-center justify-center gap-1.5 leading-tight ${className}`} style={{ color: AWARD_TEXT[c] }}>
      <img src={src} onError={onErr} alt="" aria-hidden="true" className="object-contain shrink-0" style={{ height: lh }} />
      <span className={`fx-prize-name uppercase font-semibold tracking-[0.16em] text-center ${wrap ? "whitespace-normal break-words" : "whitespace-nowrap"}`} style={{ fontSize: fs }}>{name}</span>
      <img src={src} onError={onErr} alt="" aria-hidden="true" className="object-contain shrink-0 -scale-x-100" style={{ height: lh }} />
    </span>
  );
};

// Neutral, anonymous "locked character" silhouette — no face, no gender cues.
export const AnonSilhouette = ({ className = "" }) => (
  <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="anon-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#1a1a1a" />
        <stop offset="1" stopColor="#0a0a0a" />
      </linearGradient>
    </defs>
    <rect width="100" height="100" fill="url(#anon-g)" />
    <g fill="#2b2b2b">
      <circle cx="50" cy="38" r="17" />
      <path d="M18 96 C 18 70, 34 60, 50 60 C 66 60, 82 70, 82 96 Z" />
    </g>
    <circle cx="50" cy="38" r="17" fill="none" stroke="var(--fx-primary, #3E9BFF)" strokeOpacity="0.25" strokeWidth="1.2" />
  </svg>
);
